"use client";
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { usePathname } from "next/navigation";
import useMenus from "@/app/lib/features/menus/menus.hook";
import MobileNavListView from "./MobileNavListView";
import MobileNavDetailView from "./MobileNavDetailView";
import { useMountTransition } from "@/app/hooks/useMountTransition";

interface MobileNavInnerProps {
  onClose: () => void;
  view: "list" | "detail";
  setView: (v: "list" | "detail") => void;
  activeCategoryId: number | null;
  setActiveCategoryId: (id: number | null) => void;
  activeSubIdx: number;
  setActiveSubIdx: (i: number) => void;
  /** CSS transition tetikleyicisi */
  isVisible: boolean;
}

const MobileNavInner = ({
  onClose,
  view,
  setView,
  activeCategoryId,
  setActiveCategoryId,
  activeSubIdx,
  setActiveSubIdx,
  isVisible,
}: MobileNavInnerProps) => {
  const { menus, isLoading } = useMenus();
  const safeMenus = menus ?? [];
  const pathname = usePathname();
  const isMounted = useRef(false);

  // Route değişince kapat (ilk mount'ta atla)
  useEffect(() => {
    if (!isMounted.current) { isMounted.current = true; return; }
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Body scroll kilidi
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const activeCategory = menus?.find((m) => m.id === activeCategoryId) ?? null;

  const handleCategoryClick = (id: number) => {
    setActiveCategoryId(id);
    setActiveSubIdx(0);
    setView("detail");
  };

  return (
    <>
      {/* Backdrop — CSS opacity transition */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          backgroundColor: "rgba(0,0,0,0.4)",
          opacity: isVisible ? 1 : 0,
          transition: "opacity 200ms ease",
        }}
      />

      {/* Panel — CSS translateX transition, GPU-accelerated */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 10000,
          backgroundColor: "#f1f1f1",
          display: "flex",
          flexDirection: "column",
          transform: isVisible ? "translateX(0)" : "translateX(100%)",
          transition: "transform 300ms ease-out",
          willChange: "transform",
        }}
      >
        {view === "list" ? (
          <MobileNavListView
            menus={safeMenus}
            isLoading={isLoading && !menus}
            onClose={onClose}
            onCategoryClick={handleCategoryClick}
          />
        ) : (
          <MobileNavDetailView
            menus={safeMenus}
            activeCategory={activeCategory}
            activeCategoryId={activeCategoryId}
            activeSubIdx={activeSubIdx}
            onBack={() => setView("list")}
            onClose={onClose}
            onTabClick={(id) => { setActiveCategoryId(id); setActiveSubIdx(0); }}
            onSubClick={setActiveSubIdx}
          />
        )}
      </div>
    </>
  );
};

interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
  initialCategoryId?: number | null;
}

const MobileNav = ({ isOpen, onClose, initialCategoryId }: MobileNavProps) => {
  const [view, setView] = useState<"list" | "detail">(
    initialCategoryId ? "detail" : "list"
  );
  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(
    initialCategoryId ?? null
  );
  const [activeSubIdx, setActiveSubIdx] = useState(0);

  // useMountTransition: rendered=DOM'da var mı, isVisible=CSS class tetikleyici
  const { rendered, isVisible } = useMountTransition(isOpen, 300);

  if (typeof window === "undefined") return null;
  if (!rendered) return null;

  return createPortal(
    <MobileNavInner
      onClose={onClose}
      view={view}
      setView={setView}
      activeCategoryId={activeCategoryId}
      setActiveCategoryId={setActiveCategoryId}
      activeSubIdx={activeSubIdx}
      setActiveSubIdx={setActiveSubIdx}
      isVisible={isVisible}
    />,
    document.body
  );
};

export default MobileNav;